<script setup lang="ts">
import { ADDONS, type AddonKey } from '~/composables/usePlanConfig'

/**
 * Reusable card for a standalone monthly add-on. Handles every entitlement
 * state itself (not owned / active / ending / payment issue / grandfathered)
 * so the pricing page, billing page, and feature pages all stay in sync.
 */
const props = withDefaults(defineProps<{
  addon: AddonKey
  /** 'pricing' sends signed-out visitors to signup; 'dashboard' assumes auth */
  context?: 'pricing' | 'dashboard'
  /** Hide the feature bullets for compact placements */
  compact?: boolean
}>(), {
  context: 'dashboard',
  compact: false
})

const authStore = useAuthStore()
const toast = useToast()
const { formatAddonPrice } = useCurrency()
const { trackGoal } = useDatafast()
const route = useRoute()

const config = computed(() => ADDONS[props.addon])
const state = computed(() => authStore.addonState(props.addon))
const isActive = computed(() => state.value?.active ?? false)
const isGrandfathered = computed(() => isActive.value && state.value?.source === 'grandfathered')
const isAdminGranted = computed(() => isActive.value && state.value?.source === 'admin')
// Past-due stays "fix your payment" even after access lapses - a second
// checkout would stack a new subscription on top of the unpaid one
const isPastDue = computed(() => state.value?.source === 'stripe' && state.value?.status === 'past_due')
const isEnding = computed(() => isActive.value && state.value?.source === 'stripe' && (state.value?.cancelAtPeriodEnd ?? false))

const periodEndLabel = computed(() => {
  const end = state.value?.currentPeriodEnd
  if (!end) return null
  return new Date(end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
})

const onSetupPage = computed(() => route.path === config.value.setupPath)

const loading = ref(false)

async function purchase() {
  if (!authStore.userId) {
    await navigateTo('/signup')
    return
  }
  loading.value = true
  trackGoal('checkout_started', {
    type: 'addon',
    addon: props.addon,
    context: props.context
  })
  try {
    const data = await $fetch<{ url: string }>('/api/stripe/addon-checkout', {
      method: 'POST',
      body: { addon: props.addon }
    })
    if (data.url) {
      await navigateTo(data.url, { external: true })
    }
  } catch (err: any) {
    toast.add({
      title: 'Error',
      description: err.data?.message || 'Failed to start checkout. Please try again.',
      icon: 'i-lucide-x',
      color: 'error'
    })
  } finally {
    loading.value = false
  }
}

async function openPortal() {
  loading.value = true
  try {
    const data = await $fetch<{ url: string }>('/api/stripe/portal', { method: 'POST' })
    if (data.url) {
      await navigateTo(data.url, { external: true })
    }
  } catch (err: any) {
    toast.add({
      title: 'Error',
      description: err.data?.message || 'Failed to open billing portal.',
      icon: 'i-lucide-x',
      color: 'error'
    })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="relative flex flex-col rounded-2xl border border-teal-400/20 bg-teal-500/4 p-6 shadow-(--surface-shadow)">
    <!-- Name + state badge -->
    <div class="flex items-center flex-wrap gap-2 mb-2">
      <UIcon
        :name="config.icon"
        class="size-5 text-teal-400"
      />
      <h3 class="text-xl font-bold text-highlighted">
        {{ config.name }}
      </h3>
      <span class="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-teal-500/20 text-teal-400 whitespace-nowrap shrink-0">
        Add-on
      </span>
      <UBadge
        v-if="isPastDue"
        color="error"
        variant="subtle"
        size="xs"
      >
        Payment issue
      </UBadge>
      <UBadge
        v-else-if="isEnding"
        color="warning"
        variant="subtle"
        size="xs"
      >
        Ending
      </UBadge>
      <UBadge
        v-else-if="isGrandfathered || isAdminGranted"
        color="success"
        variant="subtle"
        size="xs"
      >
        Included free
      </UBadge>
      <UBadge
        v-else-if="isActive"
        color="success"
        variant="subtle"
        size="xs"
      >
        Active
      </UBadge>
    </div>

    <p class="text-sm text-muted mb-5">
      {{ config.tagline }}
    </p>

    <!-- Price -->
    <div class="flex items-baseline gap-1 mb-5">
      <span class="text-4xl font-bold text-highlighted">{{ formatAddonPrice(addon) }}</span>
      <span class="text-sm text-dimmed">/month</span>
    </div>

    <!-- CTA per state -->
    <div class="mb-6 flex flex-col gap-2">
      <template v-if="!isActive && !isPastDue">
        <UButton
          :label="authStore.userId ? `Add for ${formatAddonPrice(addon)}/month` : 'Get Started'"
          color="primary"
          variant="solid"
          block
          size="lg"
          :loading="loading"
          @click="purchase"
        />
      </template>
      <template v-else-if="isPastDue">
        <UButton
          label="Update payment method"
          color="error"
          variant="solid"
          block
          size="lg"
          :loading="loading"
          @click="openPortal"
        />
        <p class="text-xs text-error-300 text-center">
          Your last payment failed. Update your payment method to keep the add-on.
        </p>
      </template>
      <template v-else-if="isEnding">
        <UButton
          label="Resume add-on"
          color="warning"
          variant="soft"
          block
          size="lg"
          :loading="loading"
          @click="openPortal"
        />
        <p class="text-xs text-warning-300 text-center">
          Ends on {{ periodEndLabel }}. Your domain will be disconnected then.
        </p>
      </template>
      <template v-else-if="isGrandfathered">
        <p class="text-xs text-dimmed text-center py-2">
          Included free with your currently connected domain. If you disconnect it, you will need the add-on to connect a new one.
        </p>
      </template>
      <template v-else>
        <UButton
          v-if="!onSetupPage"
          :label="config.setupLabel"
          color="primary"
          variant="solid"
          block
          size="lg"
          :to="config.setupPath"
        />
        <UButton
          v-if="state?.source === 'stripe'"
          label="Manage"
          color="neutral"
          variant="outline"
          block
          :loading="loading"
          @click="openPortal"
        />
        <p
          v-if="state?.source === 'stripe' && periodEndLabel"
          class="text-xs text-dimmed text-center"
        >
          Renews on {{ periodEndLabel }}
        </p>
      </template>
    </div>

    <!-- Features -->
    <ul
      v-if="!compact"
      class="flex flex-col gap-3 flex-1"
    >
      <li
        v-for="(feature, i) in config.features"
        :key="i"
        class="flex items-start gap-2.5 text-sm text-muted"
      >
        <UIcon
          name="i-lucide-check"
          class="size-4 shrink-0 mt-0.5 text-teal-400"
        />
        <span>{{ feature }}</span>
      </li>
    </ul>
  </div>
</template>
